import { useMemo, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
// Behavioural controls (popovers / form inputs) use the project ui kit — the visual redesign (table, tags,
// buttons, icons) is Tailwind, matching the TwDataTable-migrated pages (Stock, Products).
import { Select, DatePicker, Tooltip, Checkbox, useApp, Popconfirm } from '../../ui';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus, Eye, CheckCircle2, Package, Truck, Flag, FileCheck2, Check, Ban, ArrowLeftRight, Wallet,
} from 'lucide-react';
import { Dayjs } from 'dayjs';
import { disableFuture } from '../../lib/dateRange';
import { request } from '../../api/client';
import { usePermission, useAuthStore } from '../../store/auth';
import { TwDataTable, Pill, type DataTableColumn } from '../../components/TwDataTable';
import { Button } from '../../ui/Button';
import { formatIst } from '../../lib/time';
import { fmtQty } from '../../lib/format';
import { ApproveOrderModal } from './ApproveOrderModal';
import { PayOrderModal } from './PayOrderModal';

// Friendly role labels for the "Placed By" column.
const ROLE_LABEL: Record<string, string> = {
  SUPER_ADMIN: 'Super Admin', ADMIN: 'Admin', ACCOUNTS: 'Accounts',
  SALES_HEAD: 'Sales Head', SALES_PERSON: 'Sales', RETAILER: 'Retailer', FARMER: 'Farmer',
};
// Status → Pill colour (the Tailwind palette: default/green/blue/gold/orange/red/purple/lime).
const STATUS_PILL: Record<string, string> = {
  DRAFT: 'default', PLACED: 'blue', AWAITING_PRODUCTION: 'purple', IN_PRODUCTION: 'purple',
  AWAITING_TRANSFER: 'blue', APPROVED: 'blue', ACCEPTED: 'lime', PACKED: 'gold', DISPATCHED: 'orange',
  DELIVERED: 'green', INVOICED: 'purple', CANCELLED: 'red', CLOSED: 'default',
  ON_HOLD: 'orange', CORRECTION_REQUIRED: 'gold', REJECTED: 'red', BACKORDER: 'orange',
};
// Timeline dot colour per status (Tailwind bg-*).
const DOT: Record<string, string> = {
  DRAFT: 'bg-gray-400', PLACED: 'bg-sky-500', AWAITING_PRODUCTION: 'bg-violet-500', IN_PRODUCTION: 'bg-violet-500',
  AWAITING_TRANSFER: 'bg-sky-500', APPROVED: 'bg-sky-500', ACCEPTED: 'bg-lime-500', PACKED: 'bg-amber-500',
  DISPATCHED: 'bg-orange-500', DELIVERED: 'bg-brand-500', INVOICED: 'bg-violet-500', CANCELLED: 'bg-red-500', CLOSED: 'bg-gray-400',
};

// Statuses offered in the list filter — the live workflow, in flow order. Excludes DRAFT (orders are
// created PLACED, never DRAFT) and the retired production states.
const FILTER_STATUSES = [
  'PLACED', 'ON_HOLD', 'CORRECTION_REQUIRED', 'APPROVED', 'ACCEPTED',
  'AWAITING_TRANSFER', 'BACKORDER', 'PACKED', 'DISPATCHED', 'DELIVERED',
  'INVOICED', 'CLOSED', 'CANCELLED', 'REJECTED',
];

// A non-admin (retailer / sales person / sales head) can cancel an order only in these pre-dispatch
// states. Once DISPATCHED, only an admin (ORDER.EDIT) may cancel.
const PRE_DISPATCH_CANCELLABLE = ['PLACED', 'ON_HOLD', 'CORRECTION_REQUIRED', 'APPROVED', 'ACCEPTED', 'AWAITING_TRANSFER', 'BACKORDER', 'PACKED'];

const statusLabel = (s: string) => (s ?? '').replace(/_/g, ' ');
// Exact-paise money (order/invoice/payment amounts): round-off was removed, so totals carry paise
// (e.g. ₹9,080.10) — showing them rounded would hide the paise and mismatch the stored value.
const inr2 = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TH = 'px-3 py-2.5 text-left text-2xs font-semibold uppercase tracking-wide text-muted';
const TD = 'px-3 py-2.5 align-top';

// ---- Expanded row: order details + status timeline ----
function OrderExpand({ order, retailer, salesPersonName }: { order: any; retailer?: any; salesPersonName?: string }) {
  const { data: timeline, isLoading: tlLoading } = useQuery({
    queryKey: ['order-timeline', order.id],
    queryFn: () => request<any[]>({ url: `/orders/${order.id}/timeline` }),
  });
  const { data: detail } = useQuery({
    queryKey: ['order-detail', order.id],
    queryFn: () => request<any>({ url: `/orders/${order.id}` }),
  });

  const tax = Number(order.cgstAmt ?? 0) + Number(order.sgstAmt ?? 0) + Number(order.igstAmt ?? 0);
  const items = detail?.items ?? [];

  // Live stock per ordered product (across warehouses), so each line shows whether it can actually be
  // fulfilled, the available quantity, and from which warehouse(s).
  const canViewStock = usePermission('STOCK.VIEW');
  const productIds = useMemo(
    () => [...new Set(items.map((i: any) => String(i.productId)).filter(Boolean))],
    [items],
  );
  const { data: stock } = useQuery({
    queryKey: ['order-stock', order.id, productIds],
    queryFn: () => request<Record<string, { warehouses: Record<string, number>; total: number }>>({
      url: '/stock/by-product', params: { productIds: productIds.join(',') },
    }),
    enabled: canViewStock && productIds.length > 0,
  });

  const Detail = ({ label, children }: { label: string; children: any }) => (
    <div className="flex justify-between gap-3 border-b border-line/60 py-1">
      <span className="text-muted">{label}</span>
      <span className="text-right text-content">{children}</span>
    </div>
  );

  return (
    <div className="grid grid-cols-1 gap-6 p-4 lg:grid-cols-[3fr_2fr]">
      {/* Order details + line items */}
      <div>
        <div className="text-sm font-semibold text-content">Order details</div>
        <div className="mt-2 grid grid-cols-1 gap-x-6 text-sm sm:grid-cols-2">
          <Detail label="Retailer">
            {retailer ? <span><b>{retailer.companyName}</b> <span className="text-subtle">({retailer.code})</span></span> : (detail?.retailerName || order.companyName || `#${order.retailerId}`)}
          </Detail>
          <Detail label="Contact">{retailer?.contactPerson ? <>{retailer.contactPerson} · </> : ''}{retailer?.mobile ?? '—'}{retailer?.city ? ` · ${retailer.city}` : ''}</Detail>
          <Detail label="Placed by">{order.placedByName ? <>{order.placedByName}{order.placedByRole ? ` · ${ROLE_LABEL[order.placedByRole] ?? order.placedByRole}` : ''}</> : '—'}</Detail>
          <Detail label="Sales person">{salesPersonName ?? detail?.salesPersonName ?? (order.salesPersonId ? `#${order.salesPersonId}` : '—')}</Detail>
          <Detail label="Channel">{order.channel}</Detail>
          <Detail label="Subtotal">{inr2(order.subtotal)}</Detail>
          <Detail label="Discount">{inr2(order.discountAmt)}</Detail>
          <Detail label="Tax">{inr2(tax)}</Detail>
          {Math.abs(Number(order.roundOff ?? 0)) >= 0.005 && (
            <Detail label="Round off">{Number(order.roundOff) < 0 ? '−' : '+'}{inr2(Math.abs(Number(order.roundOff)))}</Detail>
          )}
          <Detail label="Grand total"><b>{inr2(order.grandTotal)}</b></Detail>
        </div>

        <div className="mt-3 overflow-x-auto rounded-control border border-line">
          <table className="w-full border-collapse text-sm">
            <thead className="bg-surface-2">
              <tr>
                <th className={TH}>Product</th>
                <th className={`${TH} text-right`}>Qty</th>
                {canViewStock && <th className={TH}>In stock</th>}
                <th className={`${TH} text-right`}>Rate</th>
                <th className={`${TH} text-right`}>Line total</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={canViewStock ? 5 : 4} className="px-3 py-6 text-center text-subtle">No line items</td></tr>
              ) : items.map((it: any) => {
                const s = stock?.[String(it.productId)];
                const total = Number(s?.total ?? 0);
                const ordered = Number(it.qty ?? 0);
                const partial = total > 0 && total < ordered;
                const color = total <= 0 ? 'red' : partial ? 'orange' : 'green';
                const label = total <= 0 ? 'Out of stock' : partial ? 'Partial' : 'In stock';
                const whs = s ? Object.entries(s.warehouses) : [];
                return (
                  <tr key={it.id} className="border-t border-line">
                    <td className={TD}>
                      {it.productNameSnapshot}{it.variantLabel ? ` ${it.variantLabel}` : ''}
                      {it.sku ? <span className="text-xs text-subtle"> · {it.sku}</span> : null}
                    </td>
                    <td className={`${TD} text-right`}>{fmtQty(it.qty)}</td>
                    {canViewStock && (
                      <td className={TD}>
                        {!stock ? <span className="text-subtle">—</span> : (
                          <div className="flex flex-col gap-1">
                            <span className="inline-flex flex-wrap items-center gap-1">
                              <Pill color={color}>{label}</Pill>
                              {partial && <Pill color="red">Short by {fmtQty(ordered - total)}</Pill>}
                              <span className="text-muted">{partial ? `${fmtQty(total)} / ${fmtQty(ordered)}` : `${fmtQty(total)} avail`}</span>
                            </span>
                            {whs.length > 0 && (
                              <span className="inline-flex flex-wrap items-center gap-1">
                                {whs.map(([code, q]) => <Pill key={code} color="blue">{code}: {fmtQty(q as number)}</Pill>)}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                    )}
                    <td className={`${TD} text-right`}>{inr2(it.unitPrice)}</td>
                    <td className={`${TD} text-right`}><b>{inr2(it.lineTotal)}</b></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Status timeline */}
      <div>
        <div className="text-sm font-semibold text-content">Status timeline</div>
        <div className="mt-3">
          {tlLoading ? (
            <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-8 animate-pulse rounded bg-surface-3" />)}</div>
          ) : (timeline ?? []).length === 0 ? (
            <div className="py-6 text-center text-sm text-subtle">No history yet</div>
          ) : (
            <ol className="relative ml-1 space-y-4 border-l border-line pl-5">
              {(timeline ?? []).map((t: any) => (
                <li key={t.id} className="relative">
                  <span className={`absolute -left-6.25 top-1 h-2.5 w-2.5 rounded-full ring-2 ring-surface ${DOT[t.toStatus] ?? 'bg-gray-400'}`} />
                  <div className="text-sm font-semibold text-content">
                    {statusLabel(t.toStatus)}
                    {t.fromStatus && <span className="font-normal text-subtle"> (from {statusLabel(t.fromStatus)})</span>}
                  </div>
                  <div className="text-xs text-muted">
                    {formatIst(t.at)} · {t.changedByName ?? (t.changedBy ? `#${t.changedBy}` : 'system')}{t.warehouseName ? ` · ${t.warehouseName}` : ''}
                  </div>
                  {t.notes && <div className="mt-0.5 text-xs text-subtle">{t.notes}</div>}
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}

export function OrdersListPage() {
  const navigate = useNavigate();
  const canViewUsers = usePermission('USER.VIEW');
  const canViewRetailers = usePermission('RETAILER.VIEW');
  const canCreateOrder = usePermission('ORDER.CREATE');
  // PATCH /orders/:id/status is gated by ORDER.APPROVE OR ORDER.EDIT (mirrors the backend).
  const canChangeStatus = usePermission('ORDER.APPROVE') || usePermission('ORDER.EDIT');
  // Fulfilment (Pack → … → Close) is Admin-only; a Sales Head can only approve/reject/request.
  const canFulfil = usePermission('ORDER.EDIT');
  // Internal-only "Note" column (admin/SA assignment instruction) — hidden for RETAILER/FARMER.
  const userRole = useAuthStore((s) => s.user?.role);
  const isInternal = !!userRole && userRole !== 'RETAILER' && userRole !== 'FARMER';
  const canCancel = usePermission('ORDER.CANCEL') || canChangeStatus; // retailer / sales person also get ORDER.CANCEL
  const canDeliver = usePermission('ORDER.DELIVER'); // mark delivered — field action (SP/SH too)
  const canPay = usePermission('ORDER.PAY'); // retailer self-pay, or SP/SH on behalf
  const { message } = useApp();
  const qc = useQueryClient();
  const [sp] = useSearchParams();
  const [status, setStatus] = useState<string | undefined>(sp.get('status') || undefined);
  const [showClosed, setShowClosed] = useState(false);
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  // Approving requires picking the fulfilment warehouse (+ live stock plan), so the list Approve button
  // opens the same ApproveOrderModal used on the detail/approval-queue pages.
  const [approveFor, setApproveFor] = useState<any>(null);
  const [payFor, setPayFor] = useState<any>(null);
  const from = range[0]?.format('YYYY-MM-DD');
  const to = range[1]?.format('YYYY-MM-DD');
  // Deep-links like /orders?status=AWAITING_TRANSFER drive the status filter — re-synced on query change.
  useEffect(() => { setStatus(sp.get('status') || undefined); }, [sp]);

  const { data, isLoading } = useQuery({
    queryKey: ['orders', status, showClosed, from, to],
    queryFn: () => request<any>({ url: '/orders', params: { status, includeClosed: showClosed ? 'true' : undefined, from, to, pageSize: 200 } }),
    placeholderData: keepPreviousData,
  });

  // Resolve retailer + sales-person ids → human details (best-effort; gated by permission).
  const { data: retailers } = useQuery({
    queryKey: ['orders-retailer-map'],
    queryFn: () => request<any>({ url: '/retailers', params: { pageSize: 200 } }),
    enabled: canViewRetailers,
  });
  const retailerMap = useMemo(() => {
    const m = new Map<string, any>();
    for (const r of retailers?.data ?? []) m.set(String(r.id), r);
    return m;
  }, [retailers]);

  const { data: users } = useQuery({
    queryKey: ['orders-user-map'],
    queryFn: () => request<any>({ url: '/users', params: { pageSize: 200 } }),
    enabled: canViewUsers,
  });
  const userMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of users?.data ?? []) m.set(String(u.id), u.fullName);
    return m;
  }, [users]);

  const rows = data?.data ?? [];

  // Inline status flip (Approve / Pack / Close / Cancel). The stock-allocating steps
  // (Dispatch / Deliver / Invoice) open the detail page instead of a bare flip.
  const change = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      request({ url: `/orders/${id}/status`, method: 'PATCH', data: { status } }),
    onSuccess: () => {
      message.success('Order status updated');
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['order-timeline'] });
      qc.invalidateQueries({ queryKey: ['order-detail'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to update status'),
  });

  // Cancel an in-progress consolidation — in-transit transfers return to source, order frees up.
  const cancelConsolidation = useMutation({
    mutationFn: (id: string) => request({ url: `/orders/${id}/consolidate/cancel`, method: 'POST' }),
    onSuccess: () => {
      message.success('Consolidation cancelled — stock returned to source warehouses');
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to cancel consolidation'),
  });

  const columns: DataTableColumn<any>[] = [
    { key: 'orderNo', title: 'Order No', dataIndex: 'orderNo', sortable: true, alwaysVisible: true, width: 170 },
    { key: 'orderDate', title: 'Date', dataIndex: 'orderDate', sortable: true, width: 160, render: (v: string) => formatIst(v) },
    {
      key: 'retailer', title: 'Retailer', alwaysVisible: true,
      render: (_: any, o: any) => {
        // Just the company name — contact / code / city / mobile all live in the expanded row, so the main row
        // stays one line and its height is governed by the density control (Compact / Default / Spacious).
        const name = retailerMap.get(String(o.retailerId))?.companyName ?? o.companyName;
        return name
          ? <span className="font-semibold text-content">{name}</span>
          : <span className="text-subtle">#{o.retailerId}</span>;
      },
    },
    {
      key: 'products', title: 'Products', width: 220, ellipsis: true,
      render: (_: any, o: any) => {
        const ps: any[] = o.products ?? [];
        if (!ps.length) return <span className="text-subtle">—</span>;
        const lbl = (p: any) => `${p.name}${p.variantLabel ? ` ${p.variantLabel}` : ''}`;
        return ps.length === 1 ? lbl(ps[0]) : (
          <Tooltip label={ps.map((p) => `${lbl(p)} × ${p.qty}`).join(', ')}>
            <span>{lbl(ps[0])} <span className="text-subtle">+{ps.length - 1}</span></span>
          </Tooltip>
        );
      },
    },
    {
      key: 'placedBy', title: 'Placed By', width: 200, alwaysVisible: true,
      // Truncate only the NAME; keep the role capsule fully visible (shrink-0) so it never gets clipped.
      render: (_: any, o: any) => (o.placedByName ? (
        <div className="flex items-center gap-1.5">
          <span className="min-w-0 truncate">{o.placedByName}</span>
          {o.placedByRole ? <Pill className="shrink-0">{ROLE_LABEL[o.placedByRole] ?? o.placedByRole}</Pill> : null}
        </div>
      ) : '—'),
    },
    { key: 'channel', title: 'Channel', dataIndex: 'channel', sortable: true, width: 110, defaultVisible: false },
    { key: 'subtotal', title: 'Subtotal', dataIndex: 'subtotal', sortable: true, align: 'right', width: 110, defaultVisible: false, render: (v: any) => inr2(v) },
    { key: 'discountAmt', title: 'Discount', dataIndex: 'discountAmt', sortable: true, align: 'right', width: 110, defaultVisible: false, render: (v: any) => inr2(v) },
    {
      key: 'grandTotal', title: 'Total', dataIndex: 'grandTotal', sortable: true, align: 'right', width: 130,
      render: (v: any, o: any) => (
        o.advanceCdStatus === 'ESTIMATED' && o.netGrandTotal != null ? (
          <Tooltip label={`Net payable ${inr2(o.netGrandTotal)} after advance cash discount (estimated · pending lock). Original ${inr2(v)}.`}>
            <div className="leading-tight">
              <b>{inr2(o.netGrandTotal)}</b>
              <div className="text-2xs text-subtle"><span className="line-through">{inr2(v)}</span></div>
            </div>
          </Tooltip>
        ) : <b>{inr2(v)}</b>
      ),
    },
    {
      key: 'status', title: 'Status', dataIndex: 'status', sortable: true, width: 120,
      render: (v: string, o: any) => {
        // "Prepaid" = the order was paid in advance and accepted (o.prepaid), regardless of CD eligibility.
        // Tint conveys the cash-discount state on top of that: green = discount locked, gold = estimated,
        // neutral = prepaid with no discount (e.g. no scheme, or an older order that billed full).
        const cd = o.advanceCdStatus;
        const showPrepaid = !!o.prepaid || cd === 'ESTIMATED' || cd === 'LOCKED';
        const prepaidColor = cd === 'LOCKED' ? 'green' : cd === 'ESTIMATED' ? 'gold' : 'default';
        const prepaidTip = cd === 'LOCKED'
          ? `Prepaid — advance cash discount locked${o.netGrandTotal ? ` · net payable ${inr2(o.netGrandTotal)}` : ''}.`
          : cd === 'ESTIMATED'
            ? `Prepaid — advance cash discount estimated${o.netGrandTotal ? ` · net payable ${inr2(o.netGrandTotal)}` : ''}, pending verification.`
            : 'Prepaid — advance payment received & accepted for this order.';
        return (
          <div className="flex flex-col items-start gap-1">
            <Pill color={STATUS_PILL[v] ?? 'default'}>{statusLabel(v)}</Pill>
            {showPrepaid && (
              <Tooltip label={prepaidTip}>
                <span className="inline-flex"><Pill color={prepaidColor}>Prepaid</Pill></span>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    ...(isInternal ? [{
      // alwaysVisible so a newly-added conditional column can't be swallowed by the table's
      // once-initialised persisted column-visibility state (or a stale localStorage entry).
      key: 'approvalNote', title: 'Note', width: 200, ellipsis: true, alwaysVisible: true,
      render: (_: any, o: any) => (o.approvalNote ? (
        <Tooltip label={<>{o.approvalNote}{o.approvalNoteBy ? <><br />— {o.approvalNoteBy}</> : null}</>}>
          <span className="text-xs text-muted">📝 {o.approvalNote}</span>
        </Tooltip>
      ) : <span className="text-subtle">—</span>),
    } as DataTableColumn<any>] : []),
    {
      key: 'due', title: 'Payment', width: 150,
      render: (_: any, o: any) => {
        const credit = Number(o.creditApplied) > 0.01
          ? <Tooltip label={`${inr2(o.creditApplied)} settled from account credit`}><span className="inline-flex"><Pill color="green">by credit</Pill></span></Tooltip>
          : null;
        if (!o.invoiceCount) return credit ?? <span className="text-subtle">—</span>;
        return (
          <span className="inline-flex items-center gap-1">
            {o.dueCleared ? (
              <Pill color="green"><Check className="size-3" /> Due cleared</Pill>
            ) : (
              <Tooltip label={`Paid ${inr2(o.invoicePaid)} of ${inr2(o.invoiceBilled)} — ${inr2(o.invoiceBalance)} outstanding`}>
                <span className="inline-flex"><Pill color="red">{inr2(o.invoiceBalance)} due</Pill></span>
              </Tooltip>
            )}
            {credit}
          </span>
        );
      },
    },
    {
      key: 'actions', title: 'Actions', width: 320, align: 'right', alwaysVisible: true,
      render: (_: any, o: any) => {
        const stop = (e: any) => e.stopPropagation();
        // Labelled "soft" actions kept COMPACT (tight padding, small icon, no shrink) so every action for a
        // row fits on ONE line — the container never wraps, and the fixed 320px column keeps all rows
        // uniform. Green ghost = a next step, soft-red = destructive; the lone solid CTA is "New order".
        const CLS = 'shrink-0 px-2! gap-1! text-sm!';
        const IC = 'size-3.5';
        const act = (key: string, label: string, icon: any, onClick: (e: any) => void) => (
          <Button key={key} variant="ghost" size="sm" className={CLS} icon={icon} onClick={onClick}>{label}</Button>
        );
        const goDetail = (key: string, label: string, icon: any) =>
          act(key, label, icon, (e) => { stop(e); navigate(`/orders/${o.id}`); });
        const cancelable = canCancel && (PRE_DISPATCH_CANCELLABLE.includes(o.status) || (canFulfil && o.status === 'DISPATCHED'));
        const cancelBtn = cancelable ? (
          <Popconfirm key="cancel" title="Cancel this order?" description="This cannot be undone."
            okText="Cancel order" okButtonProps={{ danger: true }}
            onConfirm={() => change.mutate({ id: String(o.id), status: 'CANCELLED' })} onCancel={stop}>
            <Button variant="text" size="sm" className={CLS} icon={<Ban className={`${IC} text-danger-600 dark:text-danger-500`} />} onClick={stop}>Cancel</Button>
          </Popconfirm>
        ) : null;
        const flipBtn = (key: string, label: string, icon: any, to: string) => (
          <Popconfirm key={key} title={`${label}?`} okText={label}
            onConfirm={() => change.mutate({ id: String(o.id), status: to })} onCancel={stop}>
            <Button variant="ghost" size="sm" className={CLS} icon={icon} onClick={stop}>{label}</Button>
          </Popconfirm>
        );
        // Close — allowed only once the invoice balance is cleared (backend-enforced); disabled with the
        // outstanding amount otherwise.
        const closeBtn = () => (o.invoiceCount === 0 || o.dueCleared) ? flipBtn('cl', 'Close', <Check className={IC} />, 'CLOSED') : (
          <Tooltip key="cl" label={`Clear the ${inr2(o.invoiceBalance)} outstanding balance before closing`}>
            <span className="inline-flex shrink-0" onClick={stop}><Button variant="ghost" size="sm" className={CLS} icon={<Check className={IC} />} disabled>Close</Button></span>
          </Tooltip>
        );
        const approveBtn = act('ap', 'Approve', <CheckCircle2 className={IC} />, (e) => { stop(e); setApproveFor(o); });
        const isShort = o.stockComplete === false;

        const acts: any[] = [];
        // Pay — retailer self-pay (or SP/SH on behalf) whenever the order still has a due.
        if (canPay && Number(o.invoiceBalance) > 0.01 && !['DRAFT', 'CANCELLED', 'REJECTED', 'CLOSED'].includes(o.status)) {
          acts.push(
            <Tooltip key="pay" label={`Pay ${inr2(o.invoiceBalance)}`}>
              <Button variant="ghost" size="sm" className={CLS} icon={<Wallet className={IC} />} onClick={(e) => { stop(e); setPayFor(o); }}>Pay</Button>
            </Tooltip>,
          );
        }
        // Mark-delivered is a field action — available to SP/SH and admins; opens the detail to record POD.
        if (canDeliver && o.status === 'DISPATCHED') {
          acts.push(goDetail('dl', 'Deliver', <Flag className={IC} />));
        }
        if (canChangeStatus) {
          if (o.status === 'PLACED') acts.push(approveBtn, cancelBtn);
          else if (o.status === 'AWAITING_PRODUCTION') {
            if (!isShort) {
              if (o.everApproved) acts.push(...(canFulfil ? [goDetail('pk', 'Pick & pack', <Package className={IC} />)] : []), cancelBtn);
              else acts.push(approveBtn, cancelBtn);
            } else acts.push(cancelBtn);
          }
          else if (o.status === 'IN_PRODUCTION') acts.push(cancelBtn);
          // AWAITING_TRANSFER: held while consolidating — Status column shows it; keep Actions compact
          // (cancel the consolidation for admins + the view button).
          else if (o.status === 'AWAITING_TRANSFER') {
            if (canFulfil) acts.push(
              <Popconfirm key="cc" title="Cancel consolidation?" description="In-transit stock returns to source warehouses."
                okText="Cancel consolidation" okButtonProps={{ danger: true }}
                onConfirm={() => cancelConsolidation.mutate(String(o.id))} onCancel={stop}>
                <Tooltip label="Cancel consolidation"><Button variant="text" size="sm" className={CLS} icon={<ArrowLeftRight className={`${IC} text-danger-600 dark:text-danger-500`} />} onClick={stop}>Cancel</Button></Tooltip>
              </Popconfirm>,
            );
          }
          else if (o.status === 'APPROVED') {
            if (isShort) acts.push(cancelBtn);
            else acts.push(...(canFulfil ? [goDetail('pk', 'Pick & pack', <Package className={IC} />)] : []), cancelBtn);
          }
          else if (canFulfil && o.status === 'PACKED') acts.push(goDetail('dp', 'Dispatch', <Truck className={IC} />), cancelBtn);
          else if (canFulfil && o.status === 'DISPATCHED') acts.push(cancelBtn); // Deliver handled above
          else if (canFulfil && o.status === 'DELIVERED') {
            acts.push(o.invoiceCount > 0 ? closeBtn() : goDetail('iv', 'Invoice', <FileCheck2 className={IC} />));
          }
          else if (canFulfil && o.status === 'INVOICED') acts.push(closeBtn());
        } else if (cancelBtn) {
          acts.push(cancelBtn);
        }
        return (
          <div className="flex flex-nowrap items-center justify-end gap-1 whitespace-nowrap" onClick={stop}>
            {acts}
            <Button variant="text" size="sm" className={`${CLS} text-muted! hover:text-content!`} icon={<Eye className={IC} />} onClick={(e) => { stop(e); navigate(`/orders/${o.id}`); }}>View</Button>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      {/* Header row — page title + primary actions, above the filters. */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold text-content" style={{ margin: 0 }}>Orders</h1>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {canCreateOrder && (
            <Button variant="primary" size="sm" icon={<Plus className="size-4" />} onClick={() => navigate('/orders/new')}>New order</Button>
          )}
        </div>
      </div>
      <TwDataTable
        storageKey="orders-list"
        rowKey="id"
        loading={isLoading}
        dataSource={rows}
        columns={columns}
        expandable={{
          expandRowByClick: true,
          expandedRowRender: (o: any) => (
            <OrderExpand order={o} retailer={retailerMap.get(String(o.retailerId))} salesPersonName={userMap.get(String(o.salesPersonId))} />
          ),
        }}
        pagination={{ pageSize: 25, showSizeChanger: true }}
        emptyText="No orders"
        toolbarLeft={
          <>
            <Select
              placeholder="Filter status" allowClear style={{ width: 200 }} popupMatchSelectWidth={false}
              value={status} onChange={setStatus}
              options={FILTER_STATUSES.map((s) => ({ value: s, label: statusLabel(s) }))}
            />
            <DatePicker.RangePicker disabledDate={disableFuture}
              value={range as any}
              onChange={(r) => setRange((r as any) ?? [null, null])}
              allowEmpty={[true, true]}
            />
            <Checkbox checked={showClosed} onCheckedChange={(checked) => setShowClosed(checked)} label="Show closed" />
          </>
        }
      />
      <ApproveOrderModal
        open={!!approveFor}
        orderId={approveFor ? String(approveFor.id) : null}
        orderNo={approveFor?.orderNo}
        defaultWarehouseId={approveFor?.dispatchWarehouseId}
        onClose={() => setApproveFor(null)}
        onApproved={() => {
          qc.invalidateQueries({ queryKey: ['orders'] });
          qc.invalidateQueries({ queryKey: ['badges'] });
          setApproveFor(null);
        }}
      />
      {payFor && (
        <PayOrderModal open={!!payFor} orderId={String(payFor.id)} orderNo={payFor.orderNo} onClose={() => setPayFor(null)} />
      )}
    </div>
  );
}
